import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import {
  BookOpenIcon,
  PhotographIcon,
  DocumentDownloadIcon,
  ChatAlt2Icon,
  SparklesIcon,
} from '@heroicons/react/outline';

import {
  SOCIAL_STORIES_BOT_URL,
  SOCIAL_STORIES_PROMO_IMAGE,
} from '@/constants/social-stories';

const features = [
  {
    icon: ChatAlt2Icon,
    title: 'Interactive Conversation',
    description:
      'Our AI bot guides you step-by-step, asking the right questions to create the perfect story for your child.',
  },
  {
    icon: SparklesIcon,
    title: 'Custom Character Design',
    description:
      'Generate a unique illustrated character for your child and approve it before the story begins.',
  },
  {
    icon: PhotographIcon,
    title: 'Illustrated Pages',
    description:
      "Every page gets its own custom illustration featuring your child's character in the story scene.",
  },
  {
    icon: DocumentDownloadIcon,
    title: 'Downloadable PDF',
    description:
      'Get a beautifully formatted, printable PDF picture book you can share with your child.',
  },
];

const SocialStoriesBotPromo = () => {
  return (
    <section className='relative overflow-hidden bg-white dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8'>
        {/* Header */}
        <div className='mx-auto max-w-2xl text-center'>
          <div className='mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'>
            <BookOpenIcon className='h-4 w-4' />
            New: AI Picture Book Creator
          </div>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            Create an Illustrated Social Story
          </h2>
          <p className='mt-4 text-lg text-gray-600 dark:text-slate-400'>
            Go beyond text — our AI bot creates a complete, illustrated picture
            book with a custom character for your child. Personalized, printed,
            and ready to read together.
          </p>
        </div>

        {/* Visual Preview + Features */}
        <div className='mt-16 grid items-center gap-12 lg:grid-cols-2'>
          {/* Left: Preview image */}
          <div className='relative'>
            <div className='overflow-hidden rounded-2xl shadow-lg'>
              <Image
                src={SOCIAL_STORIES_PROMO_IMAGE}
                alt='AI-generated illustrated social story example showing a child in a school cafeteria'
                width={600}
                height={600}
                className='h-auto w-full'
              />
            </div>
          </div>

          {/* Right: Features grid */}
          <div className='space-y-6'>
            {features.map((feature) => (
              <div key={feature.title} className='flex gap-4'>
                <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-600 dark:bg-indigo-500'>
                  <feature.icon className='h-5 w-5 text-white' />
                </div>
                <div>
                  <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
                    {feature.title}
                  </h3>
                  <p className='mt-1 text-sm text-gray-600 dark:text-slate-400'>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}

            {/* CTA */}
            <div className='pt-4'>
              <Link
                href={SOCIAL_STORIES_BOT_URL}
                className='inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
              >
                <BookOpenIcon className='h-5 w-5' />
                Create Illustrated Story
              </Link>
              <p className='mt-3 text-sm text-gray-500 dark:text-slate-500'>
                Powered by Marky AI Agent — takes about 2 minutes
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialStoriesBotPromo;
